import {
  Info,
  Eye,
  EyeOff,
  AlertCircle,
  AlertTriangle,
  CheckCircle,
  ShieldCheck,
  Copy,
  Check,
} from "lucide-react";
import React, { forwardRef, useState } from "react";
import { cn } from "../../utils/cn";
import Tooltip from "./Tooltip";

interface InputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  label?: string;
  labelTooltip?: string;
  labelRight?: React.ReactNode;
  error?: string;
  success?: string;
  hint?: string;
  /** Aviso não bloqueante (ex: acima do limite recomendado de exibição, mas ainda dentro do limite técnico). */
  warning?: string;
  icon?: React.ReactNode;
  iconPosition?: "left" | "right";
  variant?: "default" | "filled" | "outline";
  inputSize?: "sm" | "md" | "lg";
  showPasswordToggle?: boolean;
  onPasswordToggle?: (e: React.MouseEvent<HTMLButtonElement>) => void;
  showCopyButton?: boolean;
}

const Input = forwardRef<HTMLInputElement, InputProps>(
  (
    {
      className,
      label,
      labelTooltip,
      labelRight,
      error,
      success,
      hint,
      warning,
      icon,
      iconPosition = "left",
      variant = "default",
      inputSize = "md",
      showPasswordToggle = false,
      onPasswordToggle,
      showCopyButton,
      type = "text",
      id,
      ...props
    },
    ref,
  ) => {
    const [showPassword, setShowPassword] = useState(false);
    const [copied, setCopied] = useState(false);
    const [isFocused, setIsFocused] = useState(false);

    const inputId = id || `input-${Math.random().toString(36).substr(2, 9)}`;
    // Se o tipo é controlado externamente (type não é 'password'), usar o tipo fornecido
    const inputType =
      showPasswordToggle && type === "password"
        ? showPassword
          ? "text"
          : "password"
        : type;

    const handlePasswordToggle = (e: React.MouseEvent<HTMLButtonElement>) => {
      if (onPasswordToggle) return onPasswordToggle(e);
      setShowPassword(!showPassword);
    };

    const handleCopy = async (e: React.MouseEvent) => {
      e.preventDefault();
      if (!props.value) return;
      try {
        await navigator.clipboard.writeText(String(props.value));
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      } catch (err) {
        console.error("Falha ao copiar:", err);
      }
    };

    const sizeClasses = {
      sm: "px-3.5 py-2 text-xs sm:text-sm h-10",
      md: "px-4 py-2.5 sm:py-3 text-sm sm:text-sm md:text-base h-[42px] sm:h-[46px] md:h-[50px]",
      lg: "px-5 py-3 sm:py-3.5 md:py-4 text-base sm:text-base md:text-lg h-[46px] sm:h-[50px] md:h-[56px]",
    };

    const variantClasses = {
      default: "border border-purple-300 bg-white",
      filled: "border-0 bg-gray-100 focus:bg-white",
      outline: "border-2 border-purple-300 bg-transparent",
    };

    const baseInputClasses = [
      "w-full rounded-xl transition-all duration-200",
      "focus:outline-hidden focus:ring-2 focus:ring-purple-500/20",
      "disabled:opacity-50 disabled:cursor-not-allowed",
      "placeholder:text-gray-500",
      "text-gray-900",
      "active:scale-[0.99]",
    ].join(" ");

    // Determinar padding horizontal baseado na presença de ícones/toggles
    const getHorizontalPadding = () => {
      // Se há ícone à esquerda
      if (icon && iconPosition === "left") {
        if (showPasswordToggle) {
          // Ícone à esquerda + toggle de senha
          return inputSize === "lg"
            ? "pl-12 pr-12"
            : inputSize === "sm"
              ? "pl-10 pr-10"
              : "pl-11 pr-11";
        }
        // Apenas ícone à esquerda - padding maior para evitar sobreposição
        return inputSize === "lg"
          ? "pl-12 pr-4 sm:pr-4.5 md:pr-5"
          : inputSize === "sm"
            ? "pl-10 pr-2.5 sm:pr-3"
            : "pl-11 sm:pl-12 pr-3.5 sm:pr-4 md:pr-4";
      }

      // Se há ícone à direita
      if (icon && iconPosition === "right") {
        return inputSize === "lg"
          ? "pr-12 pl-4 sm:pl-4.5 md:pl-5"
          : inputSize === "sm"
            ? "pr-10 pl-2.5 sm:pl-3"
            : "pr-11 pl-3.5 sm:pl-4 md:pl-4";
      }

      // Se há apenas toggle de senha
      if (showPasswordToggle) {
        return inputSize === "lg"
          ? "pl-4 sm:pl-4.5 md:pl-5 pr-12"
          : inputSize === "sm"
            ? "pl-2.5 sm:pl-3 pr-10"
            : "pl-3.5 sm:pl-4 md:pl-4 pr-11";
      }

      // Sem ícone nem toggle - padding padrão
      return inputSize === "lg"
        ? "px-4 sm:px-4.5 md:px-5"
        : inputSize === "sm"
          ? "px-3.5"
          : "px-3.5 sm:px-4 md:px-4";
    };

    const stateClasses = error
      ? "border-red-300 focus:border-red-500 focus:ring-red-500/20"
      : success
        ? "border-green-300 focus:border-green-500 focus:ring-green-500/20"
        : isFocused
          ? "border-purple-400 focus:border-purple-500"
          : variant === "default"
            ? "hover:border-purple-400"
            : "";

    return (
      <div
        className={`space-y-1.5 ${!label && !labelRight && !error && !success ? "space-y-0!" : ""}`}
      >
        {/* Label */}
        {(label || labelRight) && (
          <div className="flex items-center justify-between gap-2">
            {label && (
              <label
                htmlFor={inputId}
                className="flex items-center gap-1.5 text-xs sm:text-sm font-medium text-gray-700 min-w-0"
              >
                <span className="truncate min-w-0">
                  {label}
                  {props.required && (
                    <span className="text-red-500 ml-1">*</span>
                  )}
                </span>
                {labelTooltip && (
                  <Tooltip
                    content={labelTooltip}
                    position="top"
                    usePortal={true}
                    delay={100}
                  >
                    <button
                      type="button"
                      className="text-gray-400 hover:text-purple-500 transition-colors p-0.5 rounded-full hover:bg-purple-50"
                    >
                      <Info className="h-3.5 w-3.5" />
                    </button>
                  </Tooltip>
                )}
                {hint && (
                  <Tooltip content={hint}>
                    <Info className="h-3.5 w-3.5 text-gray-400 hover:text-gray-600 transition-colors cursor-help" />
                  </Tooltip>
                )}
              </label>
            )}
            {labelRight && (
              <div className="shrink-0 leading-none ml-auto">{labelRight}</div>
            )}
          </div>
        )}

        {/* Input Container */}
        <div className="relative w-full">
          {/* Left Icon */}
          {icon && iconPosition === "left" && (
            <div
              className={`absolute inset-y-0 left-0 flex items-center pointer-events-none z-10 ${
                inputSize === "lg"
                  ? "pl-3.5"
                  : inputSize === "sm"
                    ? "pl-3"
                    : "pl-3.5 sm:pl-4"
              }`}
            >
              <div
                className="text-gray-500 flex items-center justify-center shrink-0"
                style={{ width: "20px", height: "20px" }}
              >
                {icon}
              </div>
            </div>
          )}

          {/* Input */}
          <input
            ref={ref}
            id={inputId}
            type={inputType}
            className={cn(
              baseInputClasses,
              sizeClasses[inputSize],
              variantClasses[variant],
              stateClasses,
              getHorizontalPadding(),
              "relative z-0",
              className,
            )}
            onFocus={() => setIsFocused(true)}
            onBlur={() => setIsFocused(false)}
            {...props}
          />

          {/* Right Icon */}
          {icon && iconPosition === "right" && !showPasswordToggle && (
            <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
              <div className="text-gray-500">{icon}</div>
            </div>
          )}

          {/* Password Toggle */}
          {showPasswordToggle && props.value !== "********" && (
            <button
              type="button"
              className="absolute inset-y-0 right-0 flex items-center pr-3 text-gray-500 hover:text-gray-700 transition-colors z-20"
              onClick={(e) => {
                e.preventDefault();
                handlePasswordToggle(e);
              }}
              aria-label={showPassword ? "Ocultar senha" : "Mostrar senha"}
              title={
                props.value === "********"
                  ? "Esta credencial está salva no servidor e não pode ser visualizada por segurança. Para alterá-la, basta digitar uma nova."
                  : showPassword
                    ? "Ocultar senha"
                    : "Mostrar senha"
              }
            >
              {(onPasswordToggle ? type === "text" : showPassword) ? (
                <EyeOff className="h-5 w-5" />
              ) : (
                <Eye className="h-5 w-5" />
              )}
            </button>
          )}

          {/* Copy Button */}
          {showCopyButton && props.value && props.value !== "********" && (
            <button
              type="button"
              className="absolute inset-y-0 right-0 flex items-center pr-3 text-gray-400 hover:text-indigo-600 transition-colors z-20"
              onClick={handleCopy}
              title="Copiar para área de transferência"
            >
              {copied ? (
                <Check className="h-4 w-4 text-green-500" />
              ) : (
                <Copy className="h-4 w-4" />
              )}
            </button>
          )}

          {/* Secure Saved Icon */}
          {showPasswordToggle && props.value === "********" && (
            <div className="absolute inset-y-0 right-0 flex items-center pr-3 group/secure cursor-help z-20">
              <Tooltip
                content="Dado protegido: Esta credencial está salva no servidor e não pode ser visualizada por segurança. Para alterá-la, basta digitar uma nova."
                position="top"
              >
                <ShieldCheck className="h-5 w-5 text-purple-400 group-hover/secure:text-purple-600 transition-colors" />
              </Tooltip>
            </div>
          )}

          {/* Status Icons */}
          {(error || success) && !showPasswordToggle && !icon && (
            <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
              {error ? (
                <AlertCircle className="h-5 w-5 text-red-500" />
              ) : success ? (
                <CheckCircle className="h-5 w-5 text-green-500" />
              ) : null}
            </div>
          )}
        </div>

        {/* Helper Text */}
        {(error || success || warning) && (
          <div className="space-y-1">
            {error && (
              <p className="flex items-center text-sm text-red-600">
                <AlertCircle className="h-4 w-4 mr-1 shrink-0" />
                {error}
              </p>
            )}
            {success && (
              <p className="flex items-center text-sm text-green-600">
                <CheckCircle className="h-4 w-4 mr-1 shrink-0" />
                {success}
              </p>
            )}
            {warning && !error && !success && (
              <p className="flex items-center text-xs text-amber-600">
                <AlertTriangle className="h-3.5 w-3.5 mr-1 shrink-0" />
                {warning}
              </p>
            )}
          </div>
        )}
      </div>
    );
  },
);

Input.displayName = "Input";

export default Input;
